import {
  INTRANET_REACTION_EMOJI,
  intranetNewsPath,
  type IntranetFeedItem,
  type IntranetReactionKind,
} from '@gb-ticket/shared-types';
import { BookmarkIcon, EyeIcon, MessageCircleIcon, ThumbsUpIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { cn } from '@/lib/utils';
import {
  IntranetLikeNames,
  IntranetLikeNamesButton,
} from '@/products/intranet/components/intranet-like-names';
import {
  IntranetReactionPicker,
  isIntranetReactionOverlay,
} from '@/products/intranet/components/intranet-reaction-picker';
import {
  useSetIntranetReaction,
  useToggleIntranetBookmark,
  useToggleIntranetLike,
} from '@/products/intranet/features/intranet/use-intranet-engagement';

/** Like, comment count and views. The like button is never inside the card link. */
export function IntranetEngagementBar({
  item,
  flush = false,
}: {
  item: IntranetFeedItem;
  /** Detail page sits in the article flow; cards keep the horizontal inset. */
  flush?: boolean;
}) {
  const like = useToggleIntranetLike(item.slug);
  const react = useSetIntranetReaction(item.slug);
  const bookmark = useToggleIntranetBookmark(item.slug);
  const [pickerOpen, setPickerOpen] = useState(false);
  const hoverRef = useRef<HTMLDivElement>(null);
  const shown = like.isPending && like.variables !== undefined ? like.variables : item.likedByMe;
  const likeCount = displayedLikeCount(item, like.isPending, like.variables);
  const currentKind: IntranetReactionKind | null =
    react.isPending && react.variables
      ? react.variables
      : (item.myReaction ?? (shown ? 'like' : null));
  const saved =
    bookmark.isPending && bookmark.variables !== undefined
      ? bookmark.variables.bookmarked
      : item.bookmarkedByMe;
  const reactionsOn = item.reactionsEnabled !== false;
  const commentsOn = item.commentsEnabled !== false;

  useEffect(() => {
    if (!pickerOpen) {
      return;
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (
        !(target instanceof Node) ||
        hoverRef.current?.contains(target) ||
        isIntranetReactionOverlay(target)
      ) {
        return;
      }
      setPickerOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [pickerOpen]);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1 py-1 text-sm text-muted-foreground',
        flush ? '-ml-2' : 'px-2',
      )}
    >
      {reactionsOn ? (
        <div ref={hoverRef} className="relative py-1" onMouseEnter={() => setPickerOpen(true)}>
          <button
            type="button"
            aria-pressed={shown}
            aria-label={`Gefällt mir, ${likeCount}`}
            className="inline-flex min-h-11 items-center gap-1 rounded-md px-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (coarsePointer()) {
                setPickerOpen(true);
                return;
              }
              like.mutate(!shown);
            }}
          >
            {currentKind ? (
              <span aria-hidden>{INTRANET_REACTION_EMOJI[currentKind]}</span>
            ) : (
              <ThumbsUpIcon className="size-4" aria-hidden />
            )}
            {likeCount}
          </button>
          {pickerOpen ? (
            <IntranetReactionPicker
              current={currentKind}
              anchor={hoverRef}
              onPick={(kind) => {
                setPickerOpen(false);
                react.mutate(kind);
              }}
            />
          ) : null}
        </div>
      ) : null}
      {commentsOn ? (
        <Link
          to={`${intranetNewsPath(item.slug)}#kommentare`}
          className="inline-flex min-h-11 items-center gap-1 rounded-md px-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          <MessageCircleIcon className="size-4" aria-hidden />
          <span className="sr-only">Kommentare</span>
          {item.commentCount}
        </Link>
      ) : null}
      <span className="inline-flex min-h-11 items-center gap-1 px-2">
        <EyeIcon className="size-4" aria-hidden />
        {item.viewCount} Aufrufe
      </span>
      <div className="ml-auto flex items-center">
        {reactionsOn ? (
          <IntranetLikeNames
            count={likeCount}
            emojis={reactionEmojis(item)}
            listPath={`/intranet/contents/${encodeURIComponent(item.slug)}/reactions`}
          />
        ) : null}
        <button
          type="button"
          aria-pressed={saved}
          aria-label={saved ? 'Gespeichert' : 'Speichern'}
          className="inline-flex min-h-11 items-center rounded-md px-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            bookmark.mutate({ bookmarked: !saved, item });
          }}
        >
          <BookmarkIcon
            className={cn('size-4', saved && 'fill-primary text-primary')}
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}

function coarsePointer(): boolean {
  return window.matchMedia?.('(pointer: coarse)').matches ?? false;
}

function reactionEmojis(item: IntranetFeedItem): string[] {
  return (item.reactions ?? []).map((row) => INTRANET_REACTION_EMOJI[row.reaction]);
}

function displayedLikeCount(
  item: IntranetFeedItem,
  pending: boolean,
  variables: boolean | undefined,
): number {
  if (!pending || variables === undefined || variables === item.likedByMe) {
    return item.likeCount;
  }
  return Math.max(0, item.likeCount + (variables ? 1 : -1));
}

const PREVIEW_ACTION =
  'inline-flex min-h-11 items-center gap-1 rounded-md px-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none';

/** Same bar as a picture for the composer preview. The buttons send nothing and navigate nowhere. */
export function IntranetEngagementPreview({
  item,
  flush = false,
}: {
  item: IntranetFeedItem;
  flush?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1 py-1 text-sm text-muted-foreground',
        flush ? '-ml-2' : 'px-2',
      )}
    >
      <button
        type="button"
        aria-pressed={item.likedByMe}
        aria-label={`Gefällt mir, ${item.likeCount}`}
        className={PREVIEW_ACTION}
      >
        <ThumbsUpIcon className="size-4" aria-hidden />
        {item.likeCount}
      </button>
      <button type="button" className={PREVIEW_ACTION}>
        <MessageCircleIcon className="size-4" aria-hidden />
        <span className="sr-only">Kommentare</span>
        {item.commentCount}
      </button>
      <span className="inline-flex min-h-11 items-center gap-1 px-2">
        <EyeIcon className="size-4" aria-hidden />
        {item.viewCount} Aufrufe
      </span>
      <div className="ml-auto flex items-center">
        <IntranetLikeNamesButton count={item.likeCount} />
        <button
          type="button"
          aria-pressed={item.bookmarkedByMe}
          aria-label="Speichern"
          className={PREVIEW_ACTION}
        >
          <BookmarkIcon className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
